// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import { ApiHandler, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { OrderPromotionModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = ApiHandler<{
    promotion: PromotionModel | OrderPromotionModel;
    creativeName: string;
}>;

const XngGaViewPromotionEvent: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const currentBillTo = getCurrentBillToState(state).value;
    const currentPage = getCurrentPage(state).type;
    const session = getSession(state);
    const locationId = session?.pickUpWarehouse?.id;
    const { promotion, creativeName } = props.parameter;

    if (state.xGoogleAnalytics.includeViewPromotionEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const cart = getCurrentCartState(props.getState()).value;

        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        const googleAnalyticsData = {
            event: GOOGLE_EVENTS.VIEW_PROMOTION,
            ecommerce: {
                creative_name: creativeName,
                creative_slot: currentPage,
                promotion_id: promotion?.id,
                promotion_name: promotion?.name,
                items: cart?.cartLines?.map(cartLine => ({
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    affiliation: currentBillTo?.companyName,
                    index: cartLine.line,
                    item_brand: cartLine.brand?.name,
                    location_id: locationId,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    ...parseProductCategories(cartLine?.properties?.categories),
                })),
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            },
        };

        dataLayer.push(googleAnalyticsData);
    }
};

export const chain = [XngGaViewPromotionEvent];

const xngGaViewPromotionEvent = createHandlerChainRunner(chain, "XngGaViewPromotionEvent");
export default xngGaViewPromotionEvent;
